import { Shield, Clock, Check, Smartphone, Camera } from 'lucide-react';

const ProtectionSection = () => {
  const protections = [
    {
      title: 'Verre Trempé',
      price: '10€',
      icon: '🛡️',
      description: 'Protection classique en verre renforcé pour votre écran.',
      details: 'Se sacrifie à la place de votre écran : le verre trempé absorbe le choc et se fissure pour protéger votre téléphone.',
      gradient: 'from-blue-500 to-indigo-600',
      bgGradient: 'from-blue-50 to-indigo-50',
    },
    {
      title: 'Hydrogel Sur Mesure',
      price: '20€',
      icon: '💧',
      description: 'Protection flexible haute résistance, découpée sur mesure.',
      details: 'Ne casse pas à l\'impact ! Le film hydrogel absorbe les chocs sans se fissurer. Idéal pour écran ou vitre arrière.',
      gradient: 'from-purple-500 to-pink-600',
      bgGradient: 'from-purple-50 to-pink-50',
      popular: true,
    },
    {
      title: 'Verre Trempé Caméra',
      price: '5€',
      icon: '📷',
      description: 'Protection dédiée pour vos objectifs photo arrière.',
      details: 'Même principe que le verre trempé écran : protège vos caméras des rayures et des chocs du quotidien.',
      gradient: 'from-cyan-500 to-teal-600',
      bgGradient: 'from-cyan-50 to-teal-50',
    },
  ];

  return (
    <section
      id="protection"
      className="section-spacing relative overflow-hidden"
      data-testid="protection-section"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-white via-green-50/30 to-white" />
      
      {/* Decorative elements */}
      <div className="absolute top-20 left-0 w-80 h-80 bg-gradient-to-br from-green-400/15 to-emerald-400/15 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-0 w-96 h-96 bg-gradient-to-br from-blue-400/10 to-cyan-400/10 rounded-full blur-3xl" />

      <div className="container-custom relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-100 to-emerald-100 rounded-full mb-6">
            <Shield size={16} className="text-green-600" />
            <span className="text-green-700 font-semibold text-sm uppercase tracking-wider" data-testid="protection-label">
              Protégez votre appareil
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6" data-testid="protection-title">
            Mieux vaut{' '}
            <span className="bg-gradient-to-r from-green-500 to-emerald-500 bg-clip-text text-transparent">prévenir</span>
            {' '}que guérir
          </h2>
          <p className="text-lg text-slate-600 mb-4" data-testid="protection-description">
            Protections d'écran et de caméra posées en boutique. 
            Évitez les mauvaises surprises !
          </p>
          
          {/* Highlight badge */}
          <div className="inline-flex items-center gap-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-3 rounded-full shadow-lg shadow-green-500/30">
            <Clock size={20} />
            <span className="font-semibold">Pose incluse en 2 min chrono</span>
            <Check size={20} />
          </div>
        </div>

        {/* Protections Grid */}
        <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto" data-testid="protection-grid">
          {protections.map((protection, index) => (
            <div
              key={index}
              className="group relative"
              data-testid={`protection-card-${index}`}
            >
              {/* Popular badge */}
              {protection.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-20">
                  <span className="px-4 py-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold rounded-full shadow-lg">
                    ⭐ Recommandé
                  </span>
                </div>
              )}
              
              {/* Card glow on hover */}
              <div className={`absolute -inset-1 bg-gradient-to-r ${protection.gradient} rounded-[28px] opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-500`} />
              
              <div className={`relative h-full bg-white rounded-3xl border-2 ${protection.popular ? 'border-purple-300' : 'border-slate-100'} shadow-lg shadow-slate-100/50 overflow-hidden hover-lift flex flex-col`}>
                {/* Header */}
                <div className={`bg-gradient-to-br ${protection.bgGradient} p-6 text-center`}>
                  <span className="text-5xl mb-4 block">{protection.icon}</span>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{protection.title}</h3>
                  <div className={`text-4xl font-bold bg-gradient-to-r ${protection.gradient} bg-clip-text text-transparent`}>
                    {protection.price}
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 flex-1 flex flex-col">
                  <p className="text-slate-700 font-medium mb-3">
                    {protection.description}
                  </p>
                  <p className="text-slate-500 text-sm leading-relaxed flex-1">
                    {protection.details}
                  </p>
                  
                  {/* CTA */}
                  <a
                    href="tel:+33983820754"
                    className={`mt-6 w-full py-3 rounded-xl font-semibold text-white bg-gradient-to-r ${protection.gradient} hover:shadow-lg transition-all hover:-translate-y-0.5 text-center block`}
                  >
                    Je veux cette protection
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom info */}
        <div className="mt-12 text-center">
          <div className="inline-flex flex-wrap justify-center gap-6 text-sm text-slate-600">
            <div className="flex items-center gap-2">
              <Check size={18} className="text-green-500" />
              <span>Pose offerte</span>
            </div>
            <div className="flex items-center gap-2">
              <Check size={18} className="text-green-500" />
              <span>Sans rendez-vous</span>
            </div>
            <div className="flex items-center gap-2">
              <Check size={18} className="text-green-500" />
              <span>Tous modèles</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProtectionSection;
